import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getTheme, hexToRgb, DARK_PRODUCT, DARK_CHROME, DARK_CHROME_VARS, DARK_STATUS_VARS } from './palette';

const THEME_STYLE_KEYS = Object.keys(DARK_CHROME_VARS).concat(Object.keys(DARK_STATUS_VARS));

function applyVars(root, vars) {
    Object.keys(vars).forEach((key) => root.style.setProperty(key, vars[key]));
}

function clearThemeStyleKeys(root) {
    THEME_STYLE_KEYS.forEach((key) => root.style.removeProperty(key));
}

function applyCorporateDark(root) {
    applyVars(root, DARK_CHROME_VARS);
    applyVars(root, DARK_STATUS_VARS);
    const rgb = hexToRgb(DARK_PRODUCT.primary);
    root.style.setProperty('--nav-bg', DARK_PRODUCT.navBg);
    root.style.setProperty('--nav-bg-mid', DARK_PRODUCT.navBg);
    root.style.setProperty('--nav-bg-end', DARK_CHROME.surfacePage);
    root.style.setProperty('--nav-fg', DARK_PRODUCT.navFg);
    root.style.setProperty('--brand-primary', DARK_PRODUCT.primary);
    root.style.setProperty('--brand-primary-mid', DARK_PRODUCT.primaryMid);
    root.style.setProperty('--brand-primary-dark', DARK_PRODUCT.primaryDark);
    root.style.setProperty('--brand-primary-light', DARK_PRODUCT.primaryLight);
    root.style.setProperty('--brand-primary-rgb', rgb);
    root.style.setProperty('--brand-primary-mid-rgb', hexToRgb(DARK_PRODUCT.primaryMid));
    root.style.setProperty('--brand-primary-dark-rgb', hexToRgb(DARK_PRODUCT.primaryDark));
    root.style.setProperty('--brand-primary-light-rgb', hexToRgb(DARK_PRODUCT.primaryLight));
    root.style.setProperty('--focus-ring', DARK_PRODUCT.primary);
    root.style.setProperty('--kpi-emphasis', DARK_PRODUCT.primary);
    root.style.setProperty('--brand-accent', DARK_PRODUCT.accent);
    root.style.setProperty('--brand-accent-rgb', hexToRgb(DARK_PRODUCT.accent));
    root.setAttribute('data-nav-theme', 'dark');
}

function restoreCouncilBrand(root) {
    clearThemeStyleKeys(root);
    const brand = getTheme();
    root.style.setProperty('--nav-bg', brand.navBg);
    root.style.setProperty('--nav-bg-mid', brand.navBgMid);
    root.style.setProperty('--nav-bg-end', brand.navBgEnd);
    root.style.setProperty('--nav-fg', brand.navFg);
    root.style.setProperty('--brand-primary', brand.primary);
    root.style.setProperty('--brand-primary-mid', brand.primaryMid);
    root.style.setProperty('--brand-primary-dark', brand.primaryDark);
    root.style.setProperty('--brand-primary-light', brand.primaryLight);
    root.style.setProperty('--brand-primary-rgb', hexToRgb(brand.primary));
    root.style.setProperty('--brand-primary-mid-rgb', hexToRgb(brand.primaryMid));
    root.style.setProperty('--brand-primary-dark-rgb', hexToRgb(brand.primaryDark));
    root.style.setProperty('--brand-primary-light-rgb', hexToRgb(brand.primaryLight));
    root.style.setProperty('--focus-ring', brand.focusRing);
    root.style.setProperty('--kpi-emphasis', '#087FC1');
    root.style.setProperty('--brand-accent', brand.accent);
    root.style.setProperty('--brand-accent-rgb', hexToRgb(brand.accent));
    root.setAttribute('data-nav-theme', brand.navTheme);
}

function applyNavForTheme(theme) {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    if (theme === 'dark') applyCorporateDark(root);
    else restoreCouncilBrand(root);
}

const THEME_KEY = 'ipsum.theme';
const VIEW_KEY = 'ipsum.viewMode';
const LEGACY_KEY = 'ipsum.displayMode';

export function isTheme(value) {
    return value === 'light' || value === 'dark';
}

export function isViewMode(value) {
    return value === 'standard' || value === 'wall';
}

function normalizePrefs(prefs) {
    let theme = isTheme(prefs && prefs.theme) ? prefs.theme : 'light';
    const viewMode = isViewMode(prefs && prefs.viewMode) ? prefs.viewMode : 'standard';
    if (viewMode === 'wall') theme = 'dark';
    return { theme, viewMode };
}

function readStoredPrefs() {
    let theme = 'light';
    let viewMode = 'standard';
    try {
        const legacy = window.localStorage.getItem(LEGACY_KEY);
        if (legacy === 'dark') theme = 'dark';
        if (legacy === 'wall') {
            theme = 'dark';
            viewMode = 'wall';
        }
        const savedTheme = window.localStorage.getItem(THEME_KEY);
        const savedView = window.localStorage.getItem(VIEW_KEY);
        if (isTheme(savedTheme)) theme = savedTheme;
        if (isViewMode(savedView)) viewMode = savedView;
        if (legacy) window.localStorage.removeItem(LEGACY_KEY);
    } catch (err) { /* keep defaults */ }
    return normalizePrefs({ theme, viewMode });
}

export function getStoredPrefs() {
    if (typeof window === 'undefined') return { theme: 'light', viewMode: 'standard' };
    return readStoredPrefs();
}

export function applyDisplayPrefs(prefs) {
    const { theme, viewMode } = normalizePrefs(prefs);
    if (typeof document === 'undefined') return { theme, viewMode };
    const root = document.documentElement;
    root.setAttribute('data-theme', theme);
    root.setAttribute('data-view', viewMode);
    root.setAttribute('data-display', viewMode === 'wall' ? 'wall' : theme);
    applyNavForTheme(theme);
    return { theme, viewMode };
}

export function mapColorScheme(theme, viewMode) {
    return theme === 'dark' || viewMode === 'wall' ? 'DARK' : 'LIGHT';
}

const DisplayModeContext = createContext({
    theme: 'light',
    viewMode: 'standard',
    setTheme: () => {},
    setViewMode: () => {},
    toggleWall: () => {},
    isDark: false,
    isWall: false
});

export function DisplayModeProvider({ children }) {
    const initial = getStoredPrefs();
    const [theme, setThemeState] = useState(initial.theme);
    const [viewMode, setViewModeState] = useState(initial.viewMode);
    const themeBeforeWallRef = useRef(initial.theme);

    useEffect(() => {
        applyDisplayPrefs({ theme, viewMode });
    }, [theme, viewMode]);

    const persist = useCallback((nextTheme, nextView) => {
        const next = normalizePrefs({ theme: nextTheme, viewMode: nextView });
        try {
            window.localStorage.setItem(THEME_KEY, next.theme);
            window.localStorage.setItem(VIEW_KEY, next.viewMode);
        } catch (err) { /* ignore */ }
        applyDisplayPrefs(next);
        return next;
    }, []);

    const setTheme = useCallback((next) => {
        if (!isTheme(next)) return;
        const leaveWall = viewMode === 'wall';
        setThemeState(next);
        if (leaveWall) setViewModeState('standard');
        persist(next, leaveWall ? 'standard' : viewMode);
    }, [persist, viewMode]);

    const setViewMode = useCallback((next) => {
        if (!isViewMode(next)) return;
        if (next === 'wall') {
            themeBeforeWallRef.current = theme;
            const applied = persist('dark', 'wall');
            setThemeState(applied.theme);
            setViewModeState(applied.viewMode);
            return;
        }
        const restored = isTheme(themeBeforeWallRef.current) ? themeBeforeWallRef.current : theme;
        const applied = persist(restored, 'standard');
        setThemeState(applied.theme);
        setViewModeState(applied.viewMode);
    }, [persist, theme]);

    const toggleWall = useCallback(() => {
        setViewMode(viewMode === 'wall' ? 'standard' : 'wall');
    }, [setViewMode, viewMode]);

    const value = useMemo(() => ({
        theme,
        viewMode,
        setTheme,
        setViewMode,
        toggleWall,
        isDark: theme === 'dark',
        isWall: viewMode === 'wall'
    }), [theme, viewMode, setTheme, setViewMode, toggleWall]);

    return (
        <DisplayModeContext.Provider value={value}>
            {children}
        </DisplayModeContext.Provider>
    );
}

export function useDisplayMode() {
    return useContext(DisplayModeContext);
}

export default DisplayModeContext;
